import { z } from "zod";

import {
  CAROUSEL_GAPS,
  DEFAULT_GAP,
  type CarouselGap,
} from "./carousel-settings";
import { categoryCardPath, readIdList } from "./category-cards";

/**
 * The Tours block — a grid of the agency's own published tours.
 *
 * THE BLOCK IS A STORED SECTION AROUND ONE DATA NODE, the Categories grid's
 * shape (`category-grid.ts`). The node carries this type and every setting:
 * the four below the query line are the QUERY, answered by traveler-api before
 * the renderer runs (`content-binding.ts`); the rest are render decisions and
 * stay out of it, so two blocks that differ only in layout are one read.
 */
export const TOUR_CARDS_TYPE = "tour-cards";

/**
 * Where a block's tours come from.
 *
 * `latest` and `booked` are feeds: the newest tours, and the ones with the
 * most confirmed bookings. `category` filters by picked destinations and
 * activity types — a tour tagged with ANY of them matches. `choose` pins a
 * hand-picked list, in the order picked.
 *
 * `latest` is the no-configuration default, so a block dropped and never
 * opened still renders something.
 */
export const TOUR_SOURCES = ["latest", "booked", "category", "choose"] as const;
export type TourSource = (typeof TOUR_SOURCES)[number];

/** A feed's default length: one more than a row of three, as the design draws. */
export const DEFAULT_TOUR_LIMIT = 4;

/**
 * The cap, and it is a payload budget rather than a taste judgement. Every
 * bound card is serialised into the page's cached API response and re-parsed on
 * every render, so an operator typing `200` would quietly turn one page into a
 * large one. Blocked at the contract, not at the renderer.
 */
export const MAX_TOUR_LIMIT = 48;

/**
 * A query, parsed from the node's attribute bag.
 *
 * TRAITS ARE STRINGS — all of them, always: GrapesJS writes what the trait
 * input holds, so `limit` arrives as `"4"`. Coercion belongs here, at the
 * contract, so no consumer has to guess whether it holds a number.
 *
 * `categories` and `tours` are comma lists of ids, read the way the Categories
 * block reads its picks (`readIdList`): ids and not slugs, so a rename never
 * empties a block, and anything that is not a uuid is dropped before it can
 * reach Postgres.
 */
export const tourCardsQuerySchema = z.object({
  source: z.enum(TOUR_SOURCES).catch("latest"),
  categories: z.unknown().optional().transform(readIdList),
  tours: z.unknown().optional().transform(readIdList),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(MAX_TOUR_LIMIT)
    .catch(DEFAULT_TOUR_LIMIT)
    .default(DEFAULT_TOUR_LIMIT),
});

export type TourCardsQuery = z.infer<typeof tourCardsQuerySchema>;

/**
 * The two facts a card can show that are not Tour Info: the leading package's
 * duration and the largest party the tour takes. Every tour carries them, so
 * they are the block's own options, listed before the agency's Tour Info.
 * (Activity was a third. It is an Activity Type, which Tour Info already
 * offers, so a block that stored it shows nothing for it.)
 */
export const TOUR_FACTS = ["duration", "group"] as const;
export type TourFact = (typeof TOUR_FACTS)[number];

export const TOUR_FACT_LABELS: Record<TourFact, string> = {
  duration: "Duration",
  group: "Group size",
};

/** Which kind of Tour Info a card item is, for the icon it falls back to. */
export type TourCardInfoKind = TourFact | "destination" | "style" | "generic";

/**
 * One item of a card's Tour Info: a built-in fact, or one of the agency's
 * Settings → Tour Info types with this tour's value for it.
 */
export interface TourCardInfo {
  /**
   * What a block's `facts` attribute stores to pick this item: the fact's
   * name, or the Tour Info type's id, which survives the type being renamed.
   */
  key: string;
  kind: TourCardInfoKind;
  /** The type's label, for example "Difficulty". */
  label: string;
  /** The tour's value, as display text. Several destinations are joined. */
  value: string;
  /**
   * A destination or style item's values, one each with the slug of its page,
   * so a card links each one and keeps them to one line with "+N more"
   * (`tourCardInfoLinkPath`). Absent on an item whose value is one piece of
   * text, and on a card cached before it existed: draw `value`.
   */
  links?: readonly TourCardDestination[];
  /**
   * The operator's icon as SVG path data, resolved on the server from its
   * `TRAVEL_ICONS` name so the card never ships that table. Subpaths are
   * separated by `|`. Null when the type has no icon: the card draws its
   * kind's fallback.
   */
  iconPath: string | null;
  /** Shown on a block that picked nothing: the type's "Show on cards". */
  onCards: boolean;
}

/** A destination a tour is tagged with: its name, and the slug of its page. */
export interface TourCardDestination {
  name: string;
  slug: string;
}

/**
 * One card, projected — NOT a tour DTO.
 *
 * The narrowness is the point. Each card rides the page's cached payload, so a
 * full experience DTO would carry itineraries, packages and facets nobody
 * renders. `slug` rather than a URL because the link is OURS to build
 * (`/tours/<slug>`) — an operator never supplies the href, which is why no
 * entry here needs scheme-checking.
 */
export interface TourCard {
  id: string;
  slug: string;
  title: string;
  imageUrl: string | null;
  /** Integer minor units, per `/CLAUDE.md`'s money rule. Null when unpriced. */
  fromPriceMinor: number | null;
  /** The price before a discount, in minor units. Null when there is none. */
  wasPriceMinor: number | null;
  currencyCode: string | null;
  /** The operator's "sold as" line, for example "per person". */
  priceUnit: string | null;
  /** The operator's marketing ribbon, for example "Best seller". */
  badge: string | null;
  /**
   * The tagged destinations, in the tour's own order. Each is drawn as a link
   * to that destination's page, so the slug travels with the name. Empty when
   * the tour is tagged with none.
   */
  destinations: readonly TourCardDestination[];
  /**
   * The text the tour index's card shows: the operator's excerpt, or else the
   * first prose paragraphs of the description. Only the horizontal layout
   * shows it.
   */
  summary: string | null;
  /**
   * The built-in facts, then the tour's Tour Info in the agency's Settings
   * order. An item the tour has no value for is left out.
   */
  info: readonly TourCardInfo[];
}

/**
 * A stable identity for a query, so two blocks asking the same thing are one
 * fetch. Built per source, so a list left over from another source (the picks
 * of a block since switched to a feed) never splits the key.
 */
export const tourCardsQueryKey = (query: TourCardsQuery): string => {
  const base = [TOUR_CARDS_TYPE, query.source];
  if (query.source === "choose") return [...base, ...query.tours].join("|");
  if (query.source === "category") {
    return [...base, ...query.categories, String(query.limit)].join("|");
  }
  return [...base, String(query.limit)].join("|");
};

/** The tour page a card links to: the storefront's own route. */
export const tourCardPath = (card: Pick<TourCard, "slug">): string =>
  `/tours/${card.slug}`;

/**
 * The page a card's destination links to: the one a Categories card for that
 * destination links to, so the two blocks can never disagree.
 */
export const tourCardDestinationPath = (
  destination: Pick<TourCardDestination, "slug">,
): string => categoryCardPath({ kind: "destination", slug: destination.slug });

/** A Tour info item's value's own page: a destination's, or a style's
 *  (an activity type, at `/tour-types/`). */
export const tourCardInfoLinkPath = (
  kind: TourCardInfoKind,
  link: Pick<TourCardDestination, "slug">,
): string =>
  categoryCardPath({
    kind: kind === "style" ? "activity" : "destination",
    slug: link.slug,
  });

/**
 * Nine arrangements of the same content. `overlay-reveal` and `inset-reveal`
 * show the destination and title, then the rest on hover or keyboard focus.
 * `horizontal` is the only one that shows the summary.
 */
export const TOUR_CARD_LAYOUTS = [
  "classic",
  "card",
  "price-image",
  "overlay",
  "overlay-reveal",
  "text-first",
  "inset",
  "inset-reveal",
  "horizontal",
] as const;

export type TourCardLayout = (typeof TOUR_CARD_LAYOUTS)[number];

/** A stored value this build does not know falls back to `classic`. */
export const readTourCardLayout = (value: unknown): TourCardLayout =>
  TOUR_CARD_LAYOUTS.find((layout) => layout === value) ?? "classic";

export const TOUR_CARD_COLUMNS = [2, 3, 4] as const;
export type TourCardColumns = (typeof TOUR_CARD_COLUMNS)[number];

/**
 * Desktop columns, for every layout. The storefront hands a number (its
 * renderer retypes numeric strings) and the canvas a string, so both are read.
 */
export const readTourCardColumns = (value: unknown): TourCardColumns =>
  TOUR_CARD_COLUMNS.find((columns) => String(columns) === String(value)) ?? 3;

export const TOUR_CARD_TABLET_COLUMNS = [1, 2, 3] as const;
export type TourCardTabletColumns = (typeof TOUR_CARD_TABLET_COLUMNS)[number];

/**
 * Tablet columns (`columns-tablet`, `device-attrs.ts`). Unset draws what the
 * block drew before the setting existed: 2, or 1 for horizontal cards, whose
 * picture and text share a row.
 */
export const readTourCardTabletColumns = (
  value: unknown,
  layout: TourCardLayout,
): TourCardTabletColumns =>
  TOUR_CARD_TABLET_COLUMNS.find((n) => String(n) === String(value)) ??
  (layout === "horizontal" ? 1 : 2);

export const TOUR_CARD_MOBILE_COLUMNS = [1, 2] as const;
export type TourCardMobileColumns = (typeof TOUR_CARD_MOBILE_COLUMNS)[number];

/**
 * Phone columns (`columns-mobile`), read only while the cards stack: the row
 * that scrolls sideways has no columns. Unset draws 1.
 */
export const readTourCardMobileColumns = (
  value: unknown,
): TourCardMobileColumns =>
  TOUR_CARD_MOBILE_COLUMNS.find((n) => String(n) === String(value)) ?? 1;

/**
 * How the cards sit on a phone. `stack` is one card per row. `scroll` is one
 * row that scrolls sideways and snaps to each card, with the next card
 * peeking in at the edge. A tablet and a desktop draw the grid either way.
 * `scroll` is the default.
 */
export const TOUR_CARD_MOBILE = ["stack", "scroll"] as const;
export type TourCardMobile = (typeof TOUR_CARD_MOBILE)[number];

/**
 * Anything unknown, and a block saved before the setting existed, reads as
 * `scroll` — the default the editor merges into that block on load, so the
 * canvas and the storefront agree.
 */
export const readTourCardMobile = (value: unknown): TourCardMobile =>
  TOUR_CARD_MOBILE.find((mobile) => mobile === value) ?? "scroll";

/**
 * The space between cards: the Categories blocks' Gap words, so every data
 * block offers the same ones. `custom` takes its length from `gap-size`
 * (`readCustomGapSize`). Anything unknown reads as `medium`, which is the
 * block's gap before the setting existed.
 */
export const readTourCardGap = (value: unknown): CarouselGap =>
  CAROUSEL_GAPS.find((gap) => gap === value) ?? DEFAULT_GAP;

/** How a card links to its tour page. The title is always a link as well. */
export const TOUR_CARD_DETAILS = ["icon", "button"] as const;
export type TourCardDetails = (typeof TOUR_CARD_DETAILS)[number];

export const readTourCardDetails = (value: unknown): TourCardDetails =>
  TOUR_CARD_DETAILS.find((details) => details === value) ?? "icon";

/**
 * The picked Tour Info keys (`TourCardInfo.key`), as stored: a comma list of
 * fact names and Tour Info type ids. Empty means nothing was picked.
 */
export const readTourInfoPicks = (value: unknown): string[] => [
  ...new Set(
    String(value ?? "")
      .split(",")
      .map((key) => key.trim())
      .filter(Boolean),
  ),
];

/**
 * The items a card draws, in the card's own order. With nothing picked it is
 * what the tour index's cards show: the duration and every type the agency
 * marked "Show on cards", without the destination, which the block has its
 * own Show destination setting for.
 */
export const pickTourInfo = (
  info: readonly TourCardInfo[],
  picks: readonly string[],
): TourCardInfo[] => {
  if (picks.length === 0) {
    return info.filter((item) => item.onCards && item.kind !== "destination");
  }
  const picked = new Set(picks);
  return info.filter((item) => picked.has(item.key));
};

/**
 * HTML boolean-attribute semantics, as `readShowTourCount` reads them:
 * presence means true, and the one spelling a round-trip can produce for "off"
 * is `false`.
 */
export const readShowDestination = (value: unknown): boolean => {
  if (value === undefined || value === null) return false;
  if (typeof value === "boolean") return value;
  return String(value).toLowerCase() !== "false";
};
